import AddressAutocomplete from '@/components/AddressAutocomplete';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import PhoneInput from '@/components/ui/phoneInput';
import { Textarea } from '@/components/ui/textarea';
import TutorialModal from '@/components/ui/TutorialModal';
import useGetAgent from '@/hooks/useGetAgent';
import useGetProfile from '@/hooks/useGetProfile';
import useRequestHelp from '@/hooks/useRequestHelp';
import useUpdateProfile from '@/hooks/useUpdateProfile';
import removeEmpty from '@/utils/removeEmpty';
import { useAuth0 } from '@auth0/auth0-react';
import { Label } from '@radix-ui/react-label';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

interface GetHelpFormData {
  name: string;
  email: string;
  phone: string;
  propertyAddress: string;
  helpNeeded: string[];
  additionalInfo: string;
  propertyId: string;
}

const helpOptions = [
  'Picking comparable sales',
  'How much of an adjustment to make',
  'Appeal procedures',
  'Technical issues',
  'Other',
];

const HelpPage = () => {
  const navigate = useNavigate();
  const [query] = useSearchParams();

  const [formData, setFormData] = useState<GetHelpFormData>({
    name: '',
    email: '',
    phone: '',
    propertyAddress: '',
    propertyId: '',
    helpNeeded: [],
    additionalInfo: '',
  });
  const [isOpenTutorial, setIsOpenTutorial] = useState(false);

  const agentIdentifier = useMemo(() => {
    const agent = query.get('agent');
    if (agent) window.localStorage.setItem('agent', agent.replace(' ', '+'));
    const localAgent = window.localStorage.getItem('agent');
    return localAgent;
  }, [query]);

  const { isAuthenticated } = useAuth0();
  const { data: agentData } = useGetAgent(agentIdentifier);
  const { data: profile } = useGetProfile();
  const { mutate: requestHelp, isPending: requestPending } = useRequestHelp();
  const { mutate: updateUser, isPending: updateLoading } = useUpdateProfile();

  const agent = useMemo(() => {
    return (
      agentData ?? {
        emailAddress: 'Support@PropertyTaxHero.com',
        phoneNumber: '303-554-7652',
        firstName: 'PropertyTaxHero',
        lastName: 'Support',
      }
    );
  }, [agentData]);

  useEffect(() => {
    if (isAuthenticated && profile && !updateLoading) {
      const user = JSON.parse(window.localStorage.getItem('user') || null);
      const userType = window.localStorage.getItem('user_type');
      const loanOfficerId = window.localStorage.getItem('loan_officer_id');
      const mortgageId = window.localStorage.getItem('mortgage_id');
      if (!!profile && (!!user || !!userType || !!loanOfficerId)) {
        updateUser(
          removeEmpty({
            firstName: user?.firstName,
            lastName: user?.lastName,
            phoneNumber: user?.phoneNumber,
            role: userType,
            loanOfficerId,
            mortgageId,
          }),
        );
      }
    }
  }, [isAuthenticated, profile, updateLoading, updateUser]);

  useEffect(() => {
    if (profile) {
      setFormData((prev) => ({
        name: `${profile?.firstName} ${profile?.lastName}`,
        email: profile?.emailAddress,
        phone: profile?.phoneNumber,
        propertyAddress: prev.propertyAddress,
        helpNeeded: [],
        additionalInfo: '',
        propertyId: prev.propertyId,
      }));
    }
  }, [profile]);

  const handleCheckboxChange = (option: string, checked: boolean) => {
    setFormData((prev) => ({
      ...prev,
      helpNeeded: checked
        ? [...prev.helpNeeded, option]
        : prev.helpNeeded.filter((item) => item !== option),
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!agent) return;

    requestHelp(
      {
        agentEmailAddress: agent.emailAddress,
        agentName: `${agent?.firstName} ${agent?.lastName}`,
        clientEmailAddress: formData.email,
        clientName: formData.name,
        helpNeeded: formData.helpNeeded,
        clientPhoneNumber: formData.phone,
        propertyAddress: formData.propertyAddress,
        propertyId: formData.propertyId,
        additionalInfo: formData.additionalInfo,
      },
      {
        onSuccess: () => {
          setFormData({
            name: `${profile?.firstName} ${profile?.lastName}`,
            email: profile?.emailAddress,
            phone: profile?.phoneNumber,
            propertyAddress: '',
            helpNeeded: [],
            additionalInfo: '',
            propertyId: '',
          });

          navigate('/');
        },
      },
    );
  };

  const onSearch = (address: string, id: string) => {
    setFormData((prev) => ({
      ...prev,
      propertyAddress: address,
      propertyId: id,
    }));
  };

  return (
    <div className="space-y-4 w-full max-w-[600px] mx-auto bg-white py-6 px-4 rounded-md shadow absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
      <div className="text-sm text-muted-foreground leading-relaxed">
        <strong className="capitalize">
          {agent?.firstName} {agent?.lastName}
        </strong>{' '}
        is eager to help you. As a local real estate professional they know the
        market as well as anyone and are a valuable resource.{' '}
        <span className="capitalize">
          {agent?.firstName} {agent?.lastName}
        </span>
        's email is <strong>{agent?.emailAddress}</strong> and their cell phone
        is <strong>{agent?.phoneNumber}</strong> if you need immediate help.
        Otherwise we'll forward them the following information.
        <br />
        <br />
        <button
          onClick={() => setIsOpenTutorial(true)}
          className="text-tax-deepBlue"
        >
          Watch a video tutorial.
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="name" className="text-sm font-medium">
              Your name: <span className="text-tax-actionRed">*</span>
            </Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, name: e.target.value }))
              }
              placeholder="Enter your name"
              className="text-sm"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email" className="text-sm font-medium">
              Your email: <span className="text-tax-actionRed">*</span>
            </Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, email: e.target.value }))
              }
              placeholder="Enter your email"
              className="text-sm"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="phone" className="text-sm font-medium">
              Your phone: <span className="text-tax-actionRed">*</span>
            </Label>
            <PhoneInput
              value={formData.phone}
              name="phone"
              onChange={(val) =>
                setFormData((prev) => ({ ...prev, phone: val }))
              }
              className="text-sm"
            />
          </div>
          <div className="space-y-2 sm:col-span-1">
            <Label htmlFor="propertyAddress" className="text-sm font-medium">
              Property address: <span className="text-tax-actionRed">*</span>
            </Label>
            <AddressAutocomplete
              inputOnly
              isLoading={false}
              onSearch={onSearch}
            />
          </div>
        </div>

        <div className="space-y-3">
          <Label className="text-sm font-medium">
            What do you need help with:
          </Label>
          <div className="space-y-2">
            {helpOptions.map((option) => (
              <div key={option} className="flex items-center space-x-2">
                <Checkbox
                  id={option}
                  checked={formData.helpNeeded.includes(option)}
                  onCheckedChange={(checked) =>
                    handleCheckboxChange(option, checked as boolean)
                  }
                />
                <Label
                  htmlFor={option}
                  className="text-sm leading-none cursor-pointer"
                >
                  {option}
                </Label>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="additionalInfo" className="text-sm font-medium">
            Tell us more:
          </Label>
          <Textarea
            id="additionalInfo"
            value={formData.additionalInfo}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                additionalInfo: e.target.value,
              }))
            }
            placeholder="Please provide additional details..."
            className="min-h-[80px] text-sm resize-none"
          />
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => navigate('/')}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            size="sm"
            className="bg-warning text-warning-foreground hover:bg-warning/90"
            disabled={
              !formData.propertyAddress ||
              !formData.propertyId ||
              !formData.name ||
              !formData.email ||
              !formData.phone ||
              requestPending
            }
          >
            Submit
          </Button>
        </div>
      </form>
      <TutorialModal
        isOpen={isOpenTutorial}
        setIsOpen={() => setIsOpenTutorial(false)}
      />
    </div>
  );
};

export default HelpPage;
